/**
 * @fileoverview Organism: horizontal-scrolling category filter.
 * @module components/organisms/CategoryRow
 */

import { useMemo } from "react"

import { UNCATEGORISED } from "@/lib/domain/amortisation"
import type { Purchase } from "@/types/types"

import "./CategoryRow.css"

/** Props accepted by {@link CategoryRow}. */
export interface CategoryRowProps {
	/** Every tracked purchase; counts are derived from this, unfiltered. */
	readonly purchases: readonly Purchase[]
	/** The active filter, or null for "every category". */
	readonly selected: string | null
	/** Called with the category to filter by, or null to clear it. */
	readonly onSelect: (category: string | null) => void
}

/**
 * Category chips, one per distinct category plus "Tous".
 *
 * @param props - The collection to derive categories from and the filter
 *   state.
 * @returns The rendered chip row, or null when there is nothing to filter
 *   by (no purchases, or a single shared category).
 *
 * @example
 * <CategoryRow purchases={purchases} selected={category} onSelect={setCategory} />
 */
export function CategoryRow({
	purchases,
	selected,
	onSelect,
}: CategoryRowProps) {
	const categories = useMemo(() => {
		const counts = new Map<string, number>()

		for (const purchase of purchases) {
			const key = purchase.category ?? UNCATEGORISED
			counts.set(key, (counts.get(key) ?? 0) + 1)
		}

		return [...counts.entries()].sort((a, b) => b[1] - a[1])
	}, [purchases])

	if (categories.length < 2) return null

	return (
		<nav className="category-row" aria-label="Filtrer par catégorie">
			<ul className="category-row__list">
				<li>
					<button
						type="button"
						className={`category-chip${selected === null ? " category-chip--active" : ""}`}
						aria-pressed={selected === null}
						onClick={() => {
							onSelect(null)
						}}
					>
						Tous
						<span className="category-chip__count">
							{purchases.length}
						</span>
					</button>
				</li>
				{categories.map(([category, count]) => (
					<li key={category}>
						<button
							type="button"
							className={`category-chip${selected === category ? " category-chip--active" : ""}`}
							aria-pressed={selected === category}
							onClick={() => {
								onSelect(category)
							}}
						>
							{category}
							<span className="category-chip__count">
								{count}
							</span>
						</button>
					</li>
				))}
			</ul>
		</nav>
	)
}
